/**
 * Which site the Reports page is showing, and in whose time zone (POS2-1137 CP1).
 *
 * **The Dashboard's precedence, without the Dashboard's URL machinery.** The order is the one
 * `use-waste-filters.ts` settles on: the context switcher's site — picked deliberately, on this
 * org — outranks the site last committed on a waste page, which outranks the first site
 * alphabetically. Reports carries none of the rest of that hook: no date preset, no filters, no
 * `site_id` written into the URL. Mounting `useWasteFilters` here would seed `/reports` with a
 * date window this page has no use for and whatever was last set on Transactions, so it takes the
 * precedence and leaves the URL alone.
 *
 * The store is read through the same org tag the seed uses (POS2-967): `site_id` is org-specific,
 * so a platform admin who switched organisation must not inherit the previous org's site.
 */
import { useMemo } from "react";

import { useSitesWithActiveMenus } from "@/hooks/use-active-menus";
import { useResolvedConfig } from "@/hooks/use-resolved-config";
import { pickDefaultSiteId } from "@/lib/sites";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useWasteFiltersStore } from "@/stores/waste-filters";

export interface ReportSite {
  siteId: string | null;
  /** The resolved site's name, for the page headings. Null until the list arrives, and for a
   * viewer with no sites — the heading renders its label alone rather than an empty separator. */
  siteName: string | null;
  timezone: string;
  /** False only while the site list is still in flight — "no sites" is a settled answer. */
  isResolving: boolean;
}

export function useReportSite(): ReportSite {
  const sitesQuery = useSitesWithActiveMenus();
  const orgId = useAdminOrgStore((s) => s.orgId);
  const switcherSiteId = useAdminOrgStore((s) => s.siteId);
  const sharedSiteId = useWasteFiltersStore((s) => (s.orgId === orgId ? s.shared?.site_id : null));

  const siteId = useMemo(
    () => switcherSiteId || sharedSiteId || pickDefaultSiteId(sitesQuery.data ?? []),
    [switcherSiteId, sharedSiteId, sitesQuery.data],
  );

  // Read off the list this hook already holds (POS2-1279) — mounting the sites query a second
  // time in the page would be a second cache entry for a name that is one `.find()` away.
  const siteName = useMemo(
    () => sitesQuery.data?.find((s) => s.id === siteId)?.name ?? null,
    [sitesQuery.data, siteId],
  );

  // The chain's UTC tail applies while the config is in flight, as everywhere else.
  const resolved = useResolvedConfig("site", siteId).data?.config?.timezone?.value;

  return {
    siteId,
    siteName,
    timezone: typeof resolved === "string" ? resolved : "UTC",
    isResolving: sitesQuery.isPending,
  };
}
